/* eslint semi:0 */
/**
 * PDO is our mechanism to pass site, page, and configuration data to
 * the client. It is passed in the page head as a script with mimetype
 * "application/json". Here we read, parse it and add methods based on
 * it's payload.
 */
import { error } from './console'
import objectPath from 'object-path'
import { registerInNamespace } from './nm'
import { create as makeEmptyObj } from '../utils/object'
import { adsConfig, ADS_QUERYSTRING_OVERRIDE_PREFIXES } from '../organizedByType/config/adsConfig'
import { queryString, getPossiblyOverriddenPropByQsWithPrefix } from '../utils/window'

const PAGE_DATA_OBJECT_ID = '_page_data_object'
let pageDataObject = null
const pageDataObjectEl = document.getElementById(PAGE_DATA_OBJECT_ID) // Don't import utils/dom to avoid circular dependencies
const pageDataObjectTextcontent = pageDataObjectEl && pageDataObjectEl.textContent

try {
  pageDataObject = Object.assign(makeEmptyObj(), JSON.parse(pageDataObjectTextcontent))
} catch (ex) {
  error("Can't read or parse pageDataObject:", ex, {
    ex,
    pageDataObjectEl,
    id: PAGE_DATA_OBJECT_ID,
    pageDataObjectTextcontent,
  })
}

export const objectPathPdo = objectPath(pageDataObject)
const { get: getFromPdo } = objectPathPdo
export const get = getFromPdo

const adsConfPath = getFromPdo('page.ads_conf', {
  dfp_client_id: '175840252', // eslint-disable-line camelcase
})
const shouldShowAds = 'should_show_ads?' in adsConfPath ? adsConfPath['should_show_ads?'] : true // show ads by default!
const { original: adsConfOriginal = {} } = adsConfPath
const adsConfOriginalPath = {
  clientId: adsConfOriginal.dfp_client_id,
  property: adsConfOriginal.property,
  channel: adsConfOriginal.channel,
  pageType: adsConfOriginal.page_type,
}
const postLayout = getFromPdo('post.layout')
const POST_LAYOUTS = {
  EMBED: 'embed',
  NATIVE: 'native',
  HYBRID: 'hybrid',
  FULL: 'full',
  DRAFT: 'preview',
}

export const postTemplate = getFromPdo('post.template')
const POST_TEMPLATES = {
  SLIDESHOW: 'slideshow',
  READER: 'reader',
  PUNDIT: 'pundit',
  TV: 'tv',
  TOPX: 'topx',
}

export const excludedComponents = getFromPdo('config.excluded_components', [])

const ENVIRONMENT_NAMES = {
  PROD: 'production',
  DEV: 'development',
}

let postCategoriesSet

export function getIsEmbededPost() {
  return postLayout === POST_LAYOUTS.EMBED
}

export function getEnvironment() {
  return getFromPdo('config.env', ENVIRONMENT_NAMES.PROD)
}

export function getIsQaEnv() {
  const { DEV, PROD } = ENVIRONMENT_NAMES
  return ![DEV, PROD].includes(getEnvironment())
}

export function getIsTopX() {
  return postTemplate === POST_TEMPLATES.TOPX
}

export function getIsTv() {
  return postTemplate === POST_TEMPLATES.TV
}

export function getIsSlideshow() {
  return postTemplate === POST_TEMPLATES.SLIDESHOW
}

export function getIsPostReader() {
  return postTemplate === POST_TEMPLATES.READER
}

export function getIsPundit() {
  return postTemplate === POST_TEMPLATES.PUNDIT
}

export function getIsDraft() {
  return postLayout === POST_LAYOUTS.DRAFT
}

export function getIsNative() {
  return postLayout === POST_LAYOUTS.NATIVE
}

export function getIsHybrid() {
  return postLayout === POST_LAYOUTS.HYBRID
}

export function getIsFull() {
  return postLayout === POST_LAYOUTS.FULL
}

export function getPunditMatchTime() {
  return getFromPdo('pundit.match_date_time')
}

export function getPunditBettingProvider() {
  return getFromPdo('pundit.betting_provider_data')
}

export function getCanonicalUrl() {
  return getFromPdo('post.url')
}

export function getLeaguesForFixtures() {
  return getFromPdo('config.leagues_for_edition.edition_legaues', {})
}

export function getPunditData() {
  return {
    homeTeam: getFromPdo('pundit.home_team_name'),
    awayTeam: getFromPdo('pundit.away_team_name'),
    bettingProviderData: getFromPdo('pundit.betting_provider_data'),
    matchTime: getFromPdo('pundit.match_date_time'),
    matchId: getFromPdo('pundit.match_id'),
    punditProfile: getFromPdo('pundit.profile'),
    punditProfileName: getFromPdo('pundit.profile_name'),
    punditPredictionBackground: getFromPdo('pundit.prediction_background'),
    punditPredictionMobileBackground: getFromPdo('pundit.prediction_mobile_background'),
    punditPredictionText: getFromPdo('pundit.prediction_text'),
  }
}

export function getCampaigns() {
  return getFromPdo('config.campaigns', {})
}

export function getIsPostPage() {
  return getFromPdo('page.type') === 'post'
}

export function getPageType() {
  return getFromPdo('page.type')
}

export function getRequestUrl() {
  return getFromPdo('page.requestUrl')
}

export function getPageLocale() {
  return getFromPdo('page.locale')
}

export function getPageDomain() {
  return getFromPdo('post.author.site')
}

export function getReactionsList() {
  return getFromPdo('post.reactions')
}

export function getPostCategoriesSet() {
  return (
    postCategoriesSet ||
    (postCategoriesSet = (function lazyGetPostCategoriesSet() {
      const { overridePostCategories } = queryString
      const fakeCategories = overridePostCategories && overridePostCategories.split(',')
      return new Set(fakeCategories || getFromPdo('post.categories', []))
    })())
  )
}

export function getPostCategories() {
  return [...getPostCategoriesSet()]
}

export function getPostId() {
  return getFromPdo('post.id', 0)
}

export function isSupportedForDomain(componentName) {
  return !excludedComponents.includes(componentName)
}

export const ads = {
  adsConfPath,
  clientId: adsConfPath.dfp_client_id,
  property: adsConfPath.property,
  channel: adsConfPath.channel,
  pageType: adsConfPath.page_type,
  original: adsConfOriginalPath,
  shouldShowAds,
  isProSeven: (shouldShowAds && adsConfPath['is_pro7?']) || false,
  getPropWithQsOverride: getPossiblyOverriddenPropByQsWithPrefix.bind(
    adsConfOriginalPath,
    ADS_QUERYSTRING_OVERRIDE_PREFIXES
  ),

  get hierarchyLevels() {
    const { getPropWithQsOverride, clientId, property, channel, pageType } = this
    let staticProps = {
      delimiter: '/',
      l0: getPropWithQsOverride('clientId', clientId),
      l1: getPropWithQsOverride('property', property),
      l2: getPropWithQsOverride('channel', channel),
      l3: getPropWithQsOverride('pageType', pageType),
      format: 'DefaultFormat',
      toString: function adLevelsToString() {
        const { l0, l1, l2, l3, l4, delimiter } = this
        return [l0, l1, l2, l3, l4].join(delimiter)
      },
      get mainLevels() {
        return Object.make(
          {
            toString: function mainLevelsToString() {
              const { property, channel, pageType } = this // eslint-disable-line no-shadow
              return [property, channel, pageType].join(staticProps.delimiter)
            },
          },
          {
            property: this.l1,
            channel: this.l2,
            pageType: this.l3,
          }
        )
      },
    }
    return Object.define(staticProps, {
      set l4(format) {
        this.format = format
      },
      get l4() {
        return this.formatWithQsOverride
      },
      get formatWithQsOverride() {
        const { format } = this
        return getPropWithQsOverride(`format-${format}`, format)
      },
    })
  },
}

export function getPostMediaTypes() {
  return getFromPdo('post.template_data.media_type', [])
}

export function getPostMainMediaType() {
  return getPostMediaTypes()[0] || null
}

export function getPostBodyMediaTypes() {
  return getPostMediaTypes().slice(1)
}

export function getIsPostMainMediaImage() {
  return (getPostMainMediaType() || '').match(/image/i)
}

let publicPdo = makeEmptyObj({
  getIsEmbededPost,
  postTemplate,
  getEnvironment,
  getIsQaEnv,
  getIsTopX,
  getIsTv,
  getIsSlideshow,
  getIsPostReader,
  getIsPundit,
  getIsDraft,
  getIsNative,
  getIsHybrid,
  getIsFull,
  getPunditMatchTime,
  getPunditBettingProvider,
  getCanonicalUrl,
  getLeaguesForFixtures,
  getPunditData,
  getCampaigns,
  getIsPostPage,
  getPageType,
  getRequestUrl,
  getPageLocale,
  getPageDomain,
  getReactionsList,
  getPostCategoriesSet,
  getPostCategories,
  getPostId,
  isSupportedForDomain,
  ads,
  getPostMediaTypes,
  getPostMainMediaType,
  getPostBodyMediaTypes,
  getIsPostMainMediaImage,
})

publicPdo = Object.assign(publicPdo, objectPathPdo)
if (!publicPdo.has('config.ads')) {
  publicPdo.set('config.ads', adsConfig)
}

// TODO: Render adsConfig on server

registerInNamespace({ pdo: publicPdo })

export default publicPdo



// WEBPACK FOOTER //
// ./provolution/app/views/provolution/jsCore/application/pageDataObject.js